import { Injectable } from '@angular/core';
import { Adapter } from '../interfaces/adapter.interface';
import { ProductDeclination, ProductDeclinationAdapter } from './ProductDeclination';

export interface Brand {
  name: string;
  code: string;
  urlImage: string;
}

export interface RelatedProduct {
  id: string;
}

export interface ProductProperties {
  details_care_instructions: string[];
  washing_advice: string[];
  description_details: string[];
  selling_point: string[];
  selling_point_details: string[];
  technical_specifications: string[];
  divers: string[];
  frequency: string[];
  order_recommendation: string[];
  range_recommendation: string[];
  range_name: string[];
  theme_label: string[];
  price_t1: string[];
  price_t2: string[];
  price_t3: string[];
  url_video: string[];
}

export class Product {
  id: string;
  name: string;
  description: string;
  shortDescription: string;
  rangeCodes: string[] = [];
  mainCategoryCode: string;
  categoryCodes: string[] = [];
  vatCode: string;
  active: boolean;
  organisationId: string;
  type: string;
  eans: string[] = [];
  saleChannelCodes: string[] = [];
  brand: Brand;
  salesUnitCount: number;
  minPrice: number;
  maxPrice: number;
  tags: string[] = [];
  creationDate: Date;
  modificationDate: Date;
  productDeclinations: ProductDeclination[] = [];
  properties: ProductProperties;
  relatedProducts: RelatedProduct[] = [];
  /**
   * Selected declination (first of list by default). Not received / sent to the API.
   */
  selectedDeclination: ProductDeclination = undefined;

  constructor(id: string,
              name: string,
              description: string,
              shortDescription: string,
              rangeCodes: string[],
              mainCategoryCode: string,
              categoryCodes: string[],
              vatCode: string,
              active: boolean,
              organisationId: string,
              type: string,
              eans: string[],
              saleChannelCodes: string[],
              brand: Brand,
              salesUnitCount: number,
              minPrice: number,
              maxPrice: number,
              tags: string[],
              creationDate: Date,
              modificationDate: Date,
              productDeclinations: ProductDeclination[],
              properties: ProductProperties,
              relatedProducts: RelatedProduct[]) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.shortDescription = shortDescription;
    this.rangeCodes = rangeCodes;
    this.mainCategoryCode = mainCategoryCode;
    this.categoryCodes = categoryCodes;
    this.vatCode = vatCode;
    this.active = active;
    this.organisationId = organisationId;
    this.type = type;
    this.eans = eans;
    this.saleChannelCodes = saleChannelCodes;
    this.brand = brand;
    this.salesUnitCount = salesUnitCount;
    this.minPrice = minPrice;
    this.maxPrice = maxPrice;
    this.tags = tags;
    this.creationDate = creationDate;
    this.modificationDate = modificationDate;
    this.productDeclinations = productDeclinations;
    this.properties = properties;
    this.relatedProducts = relatedProducts;

    if (this.productDeclinations.length > 0) {
      this.selectedDeclination = productDeclinations[0];
    }
  }

  /**
   * Returns true if the product has the tag in parameter.
   * @param tagName
   */
  public hasTag(tagName: string): boolean {
    return this.tags && this.tags.indexOf(tagName) > -1;
  }

  /**
   * Returns the path of an image with hd prefix & rename _sd to _hd.
   * @param url
   */
  public getHdImageUrl(url: string) {
    url = url.replace(/_sd/ig, '_hd');
    return url.replace(/\/([^\/]*.\w+)$/ig, '/hd/$1');
  }

  /**
   * Select a specific declination considering the id provided.
   * @param declinationId
   */
  public selectDeclination(declinationId: string) {
    if (this.productDeclinations.length > 0) {
      for (const declination of this.productDeclinations) {
        if (declinationId === declination.id) {
          this.selectedDeclination = declination;
          return;
        }
      }
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class ProductAdapter implements Adapter<Product> {
  adapt(item: any): Product {
    const productDeclinationAdapter = new ProductDeclinationAdapter();

    return new Product(
      item.id,
      item.name,
      item.description,
      item.shortDescription,
      item.rangeCodes,
      item.mainCategoryCode,
      item.categoryCodes,
      item.vatCode,
      item.active,
      item.organisationId,
      item.type,
      item.eans,
      item.saleChannelCodes,
      item.brand,
      item.salesUnitCount,
      item.minPrice,
      item.maxPrice,
      item.tags,
      item.creationDate ? new Date(item.creationDate) : undefined,
      item.modificationDate ? new Date(item.modificationDate) : undefined,
      item.productDeclinations ? item.productDeclinations.map(elt => productDeclinationAdapter.adapt(elt)) : [],
      item.properties,
      item.relatedProducts ? item.relatedProducts : []
    );
  }

  prepare(object: Product): any {
    let target = {};
    Object.assign(target, object);
    delete target['productDeclinations'];

    return target;
  }
}
